import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Input from "../utils/Input";
import Button from "../utils/Button";
import { useApiQuery } from "../hooks/useApiQuery";
import toast from "react-hot-toast";

type ChangePasswordResponse = {
  message?: string;
};

const roleLoginMap: Record<string, string> = {
  admin: "/admin/login",
  partner: "/partner/login",
  canvasser: "/canvasser/login",
};

export default function ChangePasswordForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = searchParams.get("token") || "";
  const role = (searchParams.get("role") || "admin").toLowerCase();

  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const {
    refetch,
    isFetching,
  } = useApiQuery<ChangePasswordResponse>({
    queryKey: ["forgot-password-complete"],
    enabled: false,
    retry: false,
    request: {
      method: "POST",
      url: "/auth/forgot-password/complete",
      data: { password, token },
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (!token) {
      setFormError("Reset token is missing or invalid.");
      toast.error("Reset token is missing or invalid.");
      return;
    }

    if (!password || password.length < 8) {
      setFormError("Password must be at least 8 characters.");
      return;
    }

    const loadingId = toast.loading("Updating password...");

    try {
      await refetch({ throwOnError: true });
      toast.success("Password updated successfully. Please log in.");

      const loginPath = roleLoginMap[role] || "/admin/login";
      navigate(loginPath);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to update password. Please try again.";
      setFormError(message);
      toast.error(message);
    } finally {
      toast.dismiss(loadingId);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-gray-900 p-8 rounded-lg shadow-lg w-full max-w-md animate-fireGlow"
    >
      <h2 className="text-2xl text-white font-bold mb-6 text-center">Change Password</h2>
      <p className="text-sm text-white text-center pb-5 m-auto">
        Create a new password to secure your account.
      </p>

      {formError && <p className="text-red-500 text-sm mb-4">{formError}</p>}

      <Input
        label="New Password"
        placeholder="Enter new password"
        type="password"
        name="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        debounce={0}
        required
        className="mb-6"
        helper="Minimum 8 characters"
        onValidate={(val) => (val && val.length >= 8 ? null : "Password must be at least 8 characters")}
      />

      <Button
        disabled={!password || password.length < 8 || !token || isFetching}
        label={isFetching ? "Updating..." : "Update Password"}
        type="submit"
        variant="primary"
        size="extra-large"
      />
    </form>
  );
}
